import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { useSEO } from '@/hooks/useSEO.ts';
import { useAuth } from '@/hooks/useAuth';
import { useFavorites } from '@/hooks/useFavorites';
import { usePodcasts } from '@/hooks/usePodcasts';
import { useNavigate } from 'react-router-dom';

// Lazy load heavy components
const InteractiveMap = lazy(() => import('@/components/InteractiveMap'));
const FloatingActionButton = lazy(() => import('@/components/FloatingActionButton'));
const PodcastGenerator = lazy(() => import('@/components/PodcastGenerator'));

// Regular imports for small components
import AirbnbSearchBar from '@/components/AirbnbSearchBar';
import LocationSuggestions from '@/components/LocationSuggestions';
import LocationButton from '@/components/LocationButton';
import { Plus } from 'lucide-react';
import { Podcast } from '@/types/Podcast';
import { useLocation } from 'react-router-dom';
import { useNavigationState } from '@/hooks/useNavigationState';
import { useIsMobile } from '@/hooks/use-mobile';

const Map = () => {
  const {
    podcasts,
    loading,
    searchPodcasts
  } = usePodcasts();
  const [filteredPodcasts, setFilteredPodcasts] = useState<Podcast[]>([]);
  const [selectedPodcast, setSelectedPodcast] = useState<Podcast | null>(null);
  const [showPodcastGenerator, setShowPodcastGenerator] = useState(false);
  const [searchTextForPodcast, setSearchTextForPodcast] = useState<string>('');
  const [preselectedLanguage, setPreselectedLanguage] = useState<'fr' | 'en' | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  
  const { toast } = useToast();
  const { language, t } = useTranslation();
  
  // Use SEO hook for dynamic meta tags
  useSEO({
    title: language === 'FR' ? 'Carte interactive - DiskOver' : 'Interactive Map - DiskOver',
    description: language === 'FR' 
      ? 'Explorez des lieux culturels fascinants avec notre carte interactive. Découvrez des podcasts audio géolocalisés dans le monde entier.'
      : 'Explore fascinating cultural places with our interactive map. Discover geolocated audio podcasts worldwide.',
    keywords: language === 'FR' 
      ? 'carte interactive, podcasts audio, culture, voyage, géolocalisation'
      : 'interactive map, audio podcasts, culture, travel, geolocation'
  });

  const { isAuthenticated } = useAuth();
  const { favorites } = useFavorites();
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateToPlace, getPageState } = useNavigationState();
  
  // State for map position and zoom to save/restore
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [mapZoom, setMapZoom] = useState<number>(2);
  const isMobile = useIsMobile();

  // Lock body scroll on mobile to prevent page scrolling; only the map should move
  useEffect(() => {
    if (!isMobile) return;
    const original = {
      overflow: document.body.style.overflow,
      height: document.body.style.height,
      position: document.body.style.position,
      width: document.body.style.width,
      top: document.body.style.top,
    } as const;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100dvh';
    document.body.style.top = '0';
    return () => {
      document.body.style.overflow = original.overflow;
      document.body.style.height = original.height;
      document.body.style.position = original.position;
      document.body.style.width = original.width;
      document.body.style.top = original.top;
      window.scrollTo(0, 0);
    };
  }, [isMobile]);

  // Update filtered podcasts when podcasts change
  useEffect(() => {
    setFilteredPodcasts(podcasts);
  }, [podcasts]);

  // Restore state if coming back from another page
  useEffect(() => {
    const state = location.state as { restoreState?: boolean; savedState?: any } | null;
    if (state?.restoreState && state?.savedState?.mapState) {
      const { mapState } = state.savedState;
      if (mapState.center && mapState.zoom !== undefined) {
        (window as any).setMapView?.(mapState.center, mapState.zoom);
      }
      if (mapState.selectedPodcast) {
        setSelectedPodcast(mapState.selectedPodcast);
        setTimeout(() => handleMapFocus(mapState.selectedPodcast), 0);
      }
      if (mapState.filteredPodcasts) {
        setFilteredPodcasts(mapState.filteredPodcasts);
      }
      
      // Clear the navigation state to prevent re-triggering
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.state]);

  // Handle podcast generation completion
  const handlePodcastGenerated = async (newPodcast: Podcast) => {
    // Add the new podcast to current list (optimistic update)
    const updatedPodcasts = [...podcasts, newPodcast];
    setFilteredPodcasts(updatedPodcasts);

    // Select the new podcast
    setSelectedPodcast(newPodcast);

    // Focus map on the new podcast
    handleMapFocus(newPodcast);

    // Close generator modal
    setShowPodcastGenerator(false);
  };

  // Handle podcast selection - zoom on map instead of navigating
  const handlePodcastSelect = (podcast: Podcast) => {
    setSelectedPodcast(podcast);
    
    // Focus and zoom on the selected podcast on the map
    handleMapFocus(podcast);
  };

  // Handle map focus from external components
  const handleMapFocus = (podcast: Podcast) => {
    if ((window as any).mapFocusFunction) {
      (window as any).mapFocusFunction(podcast, true);
    }
  };

  // Handle search results with optimized search
  const handleSearchResults = useCallback((results: Podcast[]) => {
    setFilteredPodcasts(results);
  }, []);

  // Handle create podcast from search with location info  
  const handleCreatePodcastFromSearch = (searchText: string) => {
    setSearchTextForPodcast(searchText);
    handleCreateClick();
  };

  // Handle opening podcast generator from map with preselected language and location
  const handleOpenPodcastGenerator = (locationDetails: any) => {
    setSearchTextForPodcast(locationDetails.name);
    setShowPodcastGenerator(true);
  };

  // Handle create click
  const handleCreateClick = () => {
    setShowPodcastGenerator(true);
  };

  // Handle category toggle for selection/deselection
  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev => {
      const newSelected = prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category];
      
      // Filter podcasts based on selected categories
      if (newSelected.length === 0) {
        setFilteredPodcasts(podcasts);
      } else {
        // Define category groups (same as in LocationSuggestions)
        const categoryGroups: { [key: string]: string[] } = {
          'Christian Landmarks': ['Catholic church', 'Church', 'Basilica', 'Cathedral', 'Catholic cathedral'],
          'Museum': ['Museum', 'Art museum']
        };

        const filtered = podcasts.filter(podcast => {
          if (!podcast.Type_Google) return false;
          const podcastTypes = podcast.Type_Google.split(',').map(type => type.trim());
          
          return newSelected.some(selectedCategory => {
            // Check if selected category is a group
            if (categoryGroups[selectedCategory]) {
              // Check if any of the podcast's types match any of the group members
              return podcastTypes.some(podcastType => 
                categoryGroups[selectedCategory].includes(podcastType)
              );
            }
            
            // Direct category match
            return podcastTypes.includes(selectedCategory);
          });
        });
        setFilteredPodcasts(filtered);
      }
      
      return newSelected;
    });
  };

  // Handle location suggestion click
  const handleLocationSuggestionClick = (query: string) => {
    if (query.startsWith('__')) {
      // Special handling for favorites
      if (query === '__favorites__') {
        const favoritesPodcasts = podcasts.filter(podcast => favorites.includes(podcast.Name));
        setFilteredPodcasts(favoritesPodcasts);
        setSelectedCategories(['__favorites__']);
      }
    } else {
      // Filter by category or search term
      const results = podcasts.filter(podcast => {
        const searchLower = query.toLowerCase();
        const nameMatch = podcast.Name.toLowerCase().includes(searchLower);
        const cityMatch = podcast.City?.toLowerCase().includes(searchLower);
        const countryMatch = podcast.Country?.toLowerCase().includes(searchLower);
        
        return nameMatch || cityMatch || countryMatch;
      });
      setFilteredPodcasts(results);
    }
  };

  if (loading) {
    return (
      <div className="h-[100dvh] flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">{t('loadingAdventures')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden overscroll-none">
      {/* Full screen map */}
      <div className="absolute inset-0">
        <Suspense fallback={
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Chargement de la carte...</p>
            </div>
          </div>
        }>
          <InteractiveMap 
            podcasts={filteredPodcasts} 
            onPodcastSelect={handlePodcastSelect} 
            selectedPodcast={selectedPodcast} 
            language={language} 
            onMapFocus={handleMapFocus} 
            onOpenPodcastGenerator={handleOpenPodcastGenerator}
          />
        </Suspense>
      </div>

      {/* Floating Search Bar and Create Button - Google Maps style */}
      <div className="absolute top-4 left-4 right-4 z-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            {/* Search Bar */}
            <div className="flex-1 overflow-visible">
              <AirbnbSearchBar 
                podcasts={podcasts} 
                onSearchResults={handleSearchResults} 
                onPodcastSelect={handlePodcastSelect}
                onCreateVisit={handleCreatePodcastFromSearch}
                placeholder={language === 'FR' ? 'Diskover a place' : 'Diskover a place'} 
                language={language}
                isCompact={true}
                fullWidthOnMobile={true}
                sideMarginPx={16}
                reportResultsWhileTyping={true}
              />
            </div>
            
            {/* Create Button */}
            <Button
              onClick={handleCreateClick}
              className="h-12 px-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md hover:bg-white/95 dark:hover:bg-gray-700/95 text-gray-700 dark:text-gray-300 border border-gray-300/50 rounded-lg shadow-lg"
            >
              {language === 'FR' ? 'Nouvelle visite' : 'New visit'}
            </Button>
          </div>
        </div>
      </div>

      {/* Floating Location Suggestions - Google Maps style */}
      <div className="absolute top-24 left-4 right-4 z-50">
        <div className="max-w-4xl mx-auto">
          <Suspense fallback={<div className="h-8 bg-muted animate-pulse rounded" />}>
            <LocationSuggestions 
              podcasts={podcasts}
              favorites={favorites}
              onLocationClick={handleLocationSuggestionClick}
              language={language}
              t={t}
              selectedCategories={selectedCategories}
              onCategoryToggle={handleCategoryToggle}
            />
          </Suspense>
        </div>
      </div>

      {/* Location Button - Bottom Right */}
      <div className="absolute right-6 z-50" style={{ bottom: 'calc(var(--mobile-nav-height, 88px) + 16px)' }}>
        <Suspense fallback={null}>
          <LocationButton 
            onClick={() => {
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                  (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;
                    // Notify map to center and show user location marker
                    const evt = new CustomEvent('centerMapOnLocation', { detail: { lat, lng } });
                    document.dispatchEvent(evt);

                    if ((window as any).mapFocusFunction) {
                      (window as any).mapFocusFunction({
                        Longitude: lng,
                        Latitude: lat,
                        Name: 'Ma position'
                      } as any, false);
                    }
                  },
                  (error) => {
                    console.error('Geolocation error:', error);
                    toast({
                      title: 'Erreur de géolocalisation',
                      description: 'Impossible d\'accéder à votre position.',
                      variant: 'destructive'
                    });
                  }
                );
              }
            }}
          />
        </Suspense>
      </div>

      {/* Podcast Generator Modal */}
      {showPodcastGenerator && (
        <Suspense fallback={null}>
          <PodcastGenerator
            isOpen={showPodcastGenerator}
            onClose={() => {
              setShowPodcastGenerator(false);
              setSearchTextForPodcast('');
              setPreselectedLanguage(null);
            }}
            onPodcastGenerated={handlePodcastGenerated}
            initialSearchText={searchTextForPodcast}
            preselectedLanguage={preselectedLanguage}
          />
        </Suspense>
      )}
    </div>
  );
};

export default Map;